import { useEffect, useState } from "react";
import { useLocation } from "react-router";

export interface MissingData {
  exclusiveOrFields: Array<Array<string>>;
  missingFields: { [tabName: string]: Array<string> };
}

export function useMissingData(activeTab?: string) {
  const [missingData, setMissingData] = useState<MissingData | null>(null);
  const [missingFields, setMissingFields] = useState<Array<string> | null>(
    null
  );
  const [exclusiveOrFields, setExclusiveOrFields] = useState<Array<
    Array<string>
  > | null>(null);
  const { state: initialMissingData } = useLocation();

  useEffect(() => {
    if (isLegacyMissingData(initialMissingData)) {
      setMissingData(initialMissingData);
    }
  }, [initialMissingData]);

  useEffect(() => {
    if (missingData) {
      if (missingData.exclusiveOrFields) {
        setExclusiveOrFields(missingData.exclusiveOrFields);
      }
      if (!activeTab) {
        setMissingFields(Object.values(missingData.missingFields).flat());
      } else {
        setMissingFields(missingData.missingFields[activeTab]);
      }
    }
  }, [activeTab, missingData]);

  return { missingData, missingFields, exclusiveOrFields };
}

// useLocation().state is also used by useMissingObjectData with a different
// shape (`{ highlightMissingFieldsFromPhase }`). Guard against that shape so
// the effect below doesn't blow up on `Object.values(undefined)`.
function isLegacyMissingData(data: unknown): data is MissingData {
  return (
    typeof data === "object" &&
    data !== null &&
    ("missingFields" in data || "exclusiveOrFields" in data)
  );
}

export function useSafeMissingData() {
  try {
    return useMissingData();
  } catch {
    return {
      missingData: null,
      missingFields: null,
      exclusiveOrFields: null
    }; // Fallback in case useMissingData fails, e.g., in storybook-tests
  }
}
